import { useState, useRef, useEffect, useMemo } from 'react';
import {
  Outlet,
  Link,
  useChildMatches,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import {
  Tooltip as MuiTooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  IconButton,
  TextField,
  Collapse,
  Menu,
  MenuItem,
} from '@mui/material';
import Card from '@mui/material/Card';
import Icon from '@mui/material/Icon';

import Page from 'src/components/page/Page';
import EmptyState from 'src/components/empty-state/EmptyState';
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';

import useInboundEmails from 'src/api/core/inbound-emails/listInboundEmails';
import InboundEmailModal from 'src/components/emails/InboundEmailModal';
import { InboundEmailWorkflowsModal } from 'src/components/emails/InboundEmailWorkflowsModal';
import { BulkRetriggerEmailRulesDialog } from 'src/components/emails/BulkRetriggerEmailRulesDialog';
import { InboundEmailDTO } from 'src/types/application/InboundEmailDTO';

import { useReprocessInboundEmail } from 'src/api/communications/useReprocessInboundEmail';

import useAuth from '@/api/auth/useAuth';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  CaretDownIcon,
  GearIcon,
  ArrowCounterClockwiseIcon,
} from '@phosphor-icons/react';
import { getClassificationDisplayInfo } from 'src/utils/emailClassificationUtils';
import { InboundEmailWorkflowRunsBadge } from 'src/components/emails/InboundEmailWorkflowRunsBadge';
import { useInboundEmailWorkflowRunSummary } from 'src/api/rpc/hooks/communications/useEmailRules';
import type { InboundEmailWorkflowRunSummary } from '@erp/rpc';

const InboundEmailsPage: React.FC = () => {
  const firstChild = useChildMatches({ select: (matches) => matches[0] });
  const search = useSearch({ from: '/authenticated/inbound-emails' });
  const navigate = useNavigate({ from: '/inbound-emails' });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const isPaginating = useRef(false);
  const { currentUser } = useAuth();
  const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedEmail, setSelectedEmail] = useState<InboundEmailDTO | null>(
    null,
  );
  const [workflowPreviewEmailId, setWorkflowPreviewEmailId] = useState<
    string | null
  >(null);
  const [showBulkRetriggerDialog, setShowBulkRetriggerDialog] = useState(false);

  // Workflow run summaries for admin users
  const [workflowRunSummaries, setWorkflowRunSummaries] = useState<
    Record<string, InboundEmailWorkflowRunSummary>
  >({});
  const workflowRunSummaryMutation = useInboundEmailWorkflowRunSummary();

  // Search filters
  const [searchFilters, setSearchFilters] = useState({
    search: '',
    from: '',
    to: '',
    upn: '',
  });
  const [showFilters, setShowFilters] = useState(false);

  const { inboundEmailId: selectedInboundEmailId } = search;
  const reprocessEmailMutation = useReprocessInboundEmail();

  const { data, error, isLoading, isFetching } = useInboundEmails({
    page,
    pageSize,
    search: searchFilters.search.trim(),
    from: searchFilters.from.trim(),
    to: searchFilters.to.trim(),
    upn: searchFilters.upn.trim(),
  });

  // Fetch workflow run summaries for visible emails (admin only)
  const emailIds = useMemo(
    () => data?.edges?.map((e) => e.id) ?? [],
    [data?.edges],
  );

  useEffect(() => {
    if (!currentUser?.isAdmin || emailIds.length === 0) return;

    workflowRunSummaryMutation.mutate(
      { inboundEmailIds: emailIds },
      {
        onSuccess: (response) => {
          setWorkflowRunSummaries(response.summaryByInboundEmailId);
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only re-run when emailIds change
  }, [emailIds, currentUser?.isAdmin]);

  const handlePageChange = (_: unknown, newPage: number) => {
    isPaginating.current = true;
    setPage(newPage + 1);
  };

  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(1);
  };

  const handleSearchChange = (field: string, value: string) => {
    setSearchFilters((prev) => ({ ...prev, [field]: value }));
    setPage(1); // Reset to first page when searching
  };

  const handleRowClick = (emailId: string) => {
    navigate({
      to: '.',
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      search: (prev: any) => ({ ...prev, inboundEmailId: emailId }),
    });
  };

  const handleMenuOpen = (
    event: React.MouseEvent<HTMLElement>,
    email: InboundEmailDTO,
  ) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedEmail(email);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedEmail(null);
  };

  const handleOpenWorkflowPreview = () => {
    const emailId = selectedEmail?.id;
    handleMenuClose();
    if (emailId) {
      setWorkflowPreviewEmailId(emailId);
    }
  };

  const handleReprocessEmail = async (email: InboundEmailDTO) => {
    handleMenuClose();
    try {
      await reprocessEmailMutation.mutateAsync(email.id);
      // Toast is handled in the mutation hook
    } catch (error) {
      // Error toast is handled in the mutation hook
      console.error('Failed to reprocess email:', error);
    }
  };

  const renderTableHeader = () => (
    <TableHead>
      <TableRow>
        <TableCell width="50px">
          <MDTypography variant="caption" fontWeight="medium" color="text">
            #
          </MDTypography>
        </TableCell>
        <TableCell width="200px">
          <MDTypography variant="caption" fontWeight="medium" color="text">
            From
          </MDTypography>
        </TableCell>
        <TableCell width="200px">
          <MDTypography variant="caption" fontWeight="medium" color="text">
            To
          </MDTypography>
        </TableCell>
        <TableCell width="180px">
          <MDTypography variant="caption" fontWeight="medium" color="text">
            UPN / Mailbox
          </MDTypography>
        </TableCell>
        <TableCell>
          <MDTypography variant="caption" fontWeight="medium" color="text">
            Subject
          </MDTypography>
        </TableCell>
        <TableCell width="150px">
          <MDTypography variant="caption" fontWeight="medium" color="text">
            Classification
          </MDTypography>
        </TableCell>
        <TableCell width="150px">
          <MDTypography variant="caption" fontWeight="medium" color="text">
            Received Date
          </MDTypography>
        </TableCell>
        <TableCell width="60px" align="center"></TableCell>
      </TableRow>
    </TableHead>
  );

  const renderTableBody = () => {
    if (!data?.edges?.length) {
      return (
        <TableBody>
          <TableRow>
            <TableCell colSpan={8}>
              <EmptyState
                icon="email"
                title="No Inbound Emails Found"
                description="There are currently no inbound emails to display"
              />
            </TableCell>
          </TableRow>
        </TableBody>
      );
    }

    return (
      <TableBody>
        {data.edges.map((email, index) => {
          const absoluteIndex = (page - 1) * pageSize + index + 1;

          return (
            <TableRow
              key={email.id}
              hover
              onClick={() => handleRowClick(email.id)}
              sx={{ cursor: 'pointer' }}
            >
              <TableCell>
                <MDTypography variant="caption">{absoluteIndex}</MDTypography>
              </TableCell>
              <TableCell>
                <MDTypography
                  variant="caption"
                  noWrap
                  sx={{
                    display: 'block',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={email.from}
                >
                  {email.from}
                </MDTypography>
              </TableCell>
              <TableCell>
                <MDTypography
                  variant="caption"
                  noWrap
                  sx={{
                    display: 'block',
                    maxWidth: '200px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                  title={email.to}
                >
                  {email.to}
                </MDTypography>
              </TableCell>
              <TableCell>
                <MDTypography
                  variant="caption"
                  noWrap
                  sx={{
                    maxWidth: '180px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: 'block',
                  }}
                  title={email.receivedByMailbox || '-'}
                >
                  {email.receivedByMailbox || '-'}
                </MDTypography>
              </TableCell>
              <TableCell>
                <MDTypography
                  variant="caption"
                  sx={{
                    maxWidth: '300px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block',
                  }}
                >
                  {email.subject || '-'}
                </MDTypography>
              </TableCell>
              <TableCell>
                <MDBox display="flex" alignItems="center" gap={1}>
                  {email.classification ? (
                    <>
                      <MDTypography
                        variant="caption"
                        color={
                          getClassificationDisplayInfo(
                            email.classification.classificationResult,
                          ).color
                        }
                      >
                        {
                          getClassificationDisplayInfo(
                            email.classification.classificationResult,
                          ).label
                        }
                      </MDTypography>
                      {email.classification.reasoning && (
                        <MuiTooltip
                          title={
                            <MDBox p={2} sx={{ maxWidth: '300px' }}>
                              <MDTypography
                                variant="caption"
                                sx={{
                                  whiteSpace: 'pre-wrap',
                                  wordBreak: 'break-word',
                                }}
                              >
                                {email.classification.reasoning}
                              </MDTypography>
                            </MDBox>
                          }
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: 'background.paper',
                                color: 'text.primary',
                                boxShadow: 1,
                                '& .MuiTooltip-arrow': {
                                  color: 'background.paper',
                                },
                              },
                            },
                          }}
                        >
                          <Icon
                            sx={{
                              fontSize: '1rem',
                              color: 'text.secondary',
                              opacity: 0.7,
                              '&:hover': {
                                opacity: 1,
                              },
                            }}
                          >
                            help_outline
                          </Icon>
                        </MuiTooltip>
                      )}
                    </>
                  ) : (
                    <MDTypography
                      variant="caption"
                      color="text.secondary"
                      sx={{ opacity: 0.5 }}
                    >
                      Not classified
                    </MDTypography>
                  )}
                  {currentUser?.isAdmin && workflowRunSummaries[email.id] && (
                    <InboundEmailWorkflowRunsBadge
                      inboundEmailId={email.id}
                      hasAny={workflowRunSummaries[email.id].hasAny}
                      runCount={
                        workflowRunSummaries[email.id].childRunCount > 0
                          ? workflowRunSummaries[email.id].childRunCount
                          : workflowRunSummaries[email.id].topLevelRunCount
                      }
                    />
                  )}
                </MDBox>
              </TableCell>
              <TableCell>
                <MDTypography variant="caption">
                  {new Date(email.createdAt).toLocaleDateString('en-US', {
                    weekday: 'short',
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </MDTypography>
              </TableCell>
              <TableCell align="center">
                <IconButton
                  size="small"
                  onClick={(e) => handleMenuOpen(e, email)}
                  sx={{
                    color: 'text.secondary',
                    opacity: 0.6,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'primary.main',
                      backgroundColor: 'action.hover',
                      opacity: 1,
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  <Icon>more_vert</Icon>
                </IconButton>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    );
  };

  if (firstChild) {
    return <Outlet />;
  }

  return (
    <Page
      title="Inbound Emails"
      error={error ?? undefined}
      isLoading={isLoading}
    >
      <Card>
        <MDBox position="relative" p={2}>
          {/* Loading indicator - fixed at top right */}
          {isFetching && (
            <MDBox
              position="absolute"
              top={16}
              right={16}
              display="flex"
              alignItems="center"
              gap={1}
              sx={{
                zIndex: 10,
                bgcolor: 'background.paper',
                px: 2,
                py: 1,
                borderRadius: 2,
                boxShadow: 2,
              }}
            >
              <CircularProgress size={16} />
              <MDTypography variant="caption" color="text.secondary">
                Loading...
              </MDTypography>
            </MDBox>
          )}

          {/* Search filters section */}
          <MDBox mb={2}>
            <MDBox display="flex" alignItems="center" gap={2} mb={1}>
              <TextField
                size="small"
                placeholder="Search all fields..."
                value={searchFilters.search}
                onChange={(e) => handleSearchChange('search', e.target.value)}
                sx={{ flexGrow: 1, maxWidth: '400px' }}
                InputProps={{
                  startAdornment: <Icon sx={{ mr: 1 }}>search</Icon>,
                }}
              />
              <Button
                variant="ghost"
                onClick={() => setShowFilters(!showFilters)}
              >
                <Icon sx={{ mr: 1 }}>
                  {showFilters ? 'expand_less' : 'expand_more'}
                </Icon>
                Advanced Filters
              </Button>
              {(searchFilters.from ||
                searchFilters.to ||
                searchFilters.upn ||
                searchFilters.search) && (
                <Button
                  variant="ghost"
                  onClick={() =>
                    setSearchFilters({
                      search: '',
                      from: '',
                      to: '',
                      upn: '',
                    })
                  }
                >
                  <Icon sx={{ mr: 1 }}>clear</Icon>
                  Clear Filters
                </Button>
              )}

              {currentUser?.isAdmin && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" className="ml-auto">
                      Email Rules
                      <CaretDownIcon className="ml-2 h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild>
                      <Link to="/inbound-emails/email-rules">
                        <GearIcon className="mr-2 h-4 w-4" />
                        View Email Rules
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => setShowBulkRetriggerDialog(true)}
                    >
                      <ArrowCounterClockwiseIcon className="mr-2 h-4 w-4" />
                      Retrigger Email Rules
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </MDBox>
            <Collapse in={showFilters}>
              <MDBox
                display="flex"
                gap={2}
                flexWrap="wrap"
                p={2}
                sx={{
                  bgcolor: 'background.default',
                  borderRadius: 1,
                }}
              >
                <TextField
                  size="small"
                  label="From"
                  placeholder="Filter by sender..."
                  value={searchFilters.from}
                  onChange={(e) => handleSearchChange('from', e.target.value)}
                  sx={{ minWidth: '200px' }}
                />
                <TextField
                  size="small"
                  label="To"
                  placeholder="Filter by recipient..."
                  value={searchFilters.to}
                  onChange={(e) => handleSearchChange('to', e.target.value)}
                  sx={{ minWidth: '200px' }}
                />
                <TextField
                  size="small"
                  label="UPN / Mailbox"
                  placeholder="Filter by mailbox..."
                  value={searchFilters.upn}
                  onChange={(e) => handleSearchChange('upn', e.target.value)}
                  sx={{ minWidth: '200px' }}
                />
              </MDBox>
            </Collapse>
          </MDBox>

          <TableContainer sx={{ boxShadow: 'none' }}>
            <Table sx={{ boxShadow: 'none' }}>
              {renderTableHeader()}
              {renderTableBody()}
            </Table>
          </TableContainer>
          {data?.pageInfo && (
            <MDBox
              display="flex"
              justifyContent="flex-end"
              alignItems="center"
              p={2}
              gap={2}
            >
              {isFetching && <CircularProgress size={20} />}
              <TablePagination
                component="div"
                count={data.pageInfo.totalCount}
                page={page - 1} // MUI pagination is 0-based, our API is 1-based
                onPageChange={handlePageChange}
                rowsPerPage={pageSize}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={[5, 10, 20, 50]}
              />
            </MDBox>
          )}
        </MDBox>
      </Card>

      {selectedInboundEmailId && (
        <InboundEmailModal
          inboundEmailId={selectedInboundEmailId}
          emails={data?.edges || []}
          onClose={() => {
            navigate({
              to: '.',
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              search: (prev: any) => ({ ...prev, inboundEmailId: undefined }),
            });
          }}
        />
      )}

      {workflowPreviewEmailId && (
        <InboundEmailWorkflowsModal
          inboundEmailId={workflowPreviewEmailId}
          onClose={() => setWorkflowPreviewEmailId(null)}
        />
      )}

      <Menu
        anchorEl={menuAnchorEl}
        open={Boolean(menuAnchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem
          onClick={() => {
            if (selectedEmail) {
              handleMenuClose();
              handleRowClick(selectedEmail.id);
            }
          }}
        >
          <Icon sx={{ mr: 1, fontSize: '1.2rem' }}>visibility</Icon>
          Open Email
        </MenuItem>
        {currentUser?.isAdmin && selectedEmail && (
          <MenuItem onClick={handleOpenWorkflowPreview}>
            <Icon sx={{ mr: 1, fontSize: '1.2rem' }}>account_tree</Icon>
            View Email Rule Workflows
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            if (selectedEmail) {
              handleReprocessEmail(selectedEmail);
            }
          }}
          disabled={reprocessEmailMutation.isPending}
        >
          <Icon sx={{ mr: 1, fontSize: '1.2rem' }}>refresh</Icon>
          Reprocess Email
        </MenuItem>
      </Menu>

      <BulkRetriggerEmailRulesDialog
        open={showBulkRetriggerDialog}
        onOpenChange={setShowBulkRetriggerDialog}
      />
    </Page>
  );
};

export default InboundEmailsPage;
